import {ClubSubscriptionTier, ClubSubscriptionTierLabels, ClubSubscriptionTierType} from "@/constants";
import services from "@/services";
import queryKeys from "@/utilities/queryKeys";
import {useMutation, useQuery} from "@tanstack/react-query";

export interface ClubSubscriptionInfo {
    subscription_tier: ClubSubscriptionTierType;
    subscription_tier_label: string;
    subscription_status: number;
    subscription_expires_at: string | null;
    is_active: boolean;
    usage: {
        entries_per_league: { current: number; limit: number };
        active_leagues: { current: number; limit: number };
        active_tournaments: { current: number; limit: number };
    };
    features: Record<string, boolean>;
    requires_branding: boolean;
}

export function useClubSubscription(clubUid: string | undefined) {
    const subscriptionQuery = useQuery<ClubSubscriptionInfo>({
        queryKey: queryKeys.club_subscription(clubUid),
        queryFn:  async () => services.clubSubscription.info(clubUid!),
        enabled:  !!clubUid,
    });

    const checkoutMutation = useMutation({
        mutationFn: async ({plan, currencyCode, onboarding}: { plan: number; currencyCode: string; onboarding?: boolean }) => {
            return services.clubSubscription.checkout({
                club_uid:      clubUid!,
                plan,
                currency_code: currencyCode,
                onboarding,
            });
        },
    });

    const billingPortalMutation = useMutation({
        mutationFn: async () => {
            return services.clubSubscription.billingPortal(clubUid!);
        },
    });

    const info = subscriptionQuery.data;

    return {
        // Query state
        isLoading: subscriptionQuery.isLoading,
        error:     subscriptionQuery.error,
        refetch:   subscriptionQuery.refetch,

        // Subscription info
        tier:             info?.subscription_tier ?? ClubSubscriptionTier.Free,
        tierLabel:        info?.subscription_tier_label ?? ClubSubscriptionTierLabels[ClubSubscriptionTier.Free],
        isActive:         info?.is_active ?? true,
        expiresAt:        info?.subscription_expires_at,
        usage:            info?.usage,
        features:         info?.features ?? {},
        requiresBranding: info?.requires_branding ?? true,

        // Billing-status state for BillingStatusBanner. All three already existed server-side
        // and nothing rendered them.
        /*
         * ACTUAL PLAN vs EFFECTIVE TIER — see ClubSubscriptionController::getSubscriptionInfo.
         *
         * `tier` above is the EFFECTIVE one and stays that way: every gate and every hasFeature call
         * already depends on it. These are the separate question of what the club is paying for.
         *
         * Use `isSubscribed` for any sentence containing the word "subscription", and `actualTier`
         * for what is on sale. `isActive` is NOT that — it reports the effective tier being usable,
         * which is true for a club that has never paid a penny.
         */
        actualTier:       (info as any)?.plan?.actual_tier ?? ClubSubscriptionTier.Free,
        actualTierLabel:  (info as any)?.plan?.actual_tier_label,
        isSubscribed:     (info as any)?.plan?.is_subscribed ?? false,
        entitlementSource: (info as any)?.plan?.entitlement_source ?? 'free',

        isLegacyPricing:  (info as any)?.is_legacy_pricing ?? false,
        pricingModel:     (info as any)?.pricing_model,
        // Organizer path (one_off | community) and the one-off block: whether the path is
        // enforced for this club, whether the one-question screen is still owed (`ask_path`),
        // the free band and the price bands.
        organizerPath:    (info as any)?.organizer_path as 'one_off' | 'community' | undefined,
        oneOff:           (info as any)?.one_off as {enforced: boolean; ask_path: boolean; path_set_at: string | null; free_entries: number; bands: {max_entries: number | null; cents: number}[]; currency: string} | undefined,
        trial:            (info as any)?.trial,
        // The $2.99 Plus trial (PlusTrial::picture): running / cancelled / eligible.
        plusTrial:        (info as any)?.plus_trial as import('@/services').PlusTrialPicture | undefined,
        dunning:          (info as any)?.dunning,
        frozenLeagues:    (info as any)?.frozen_leagues ?? [],
        // Tournaments needing the expiry offer. Nothing freezes for a tournament, so unlike
        // frozenLeagues there is no other signal that the moment has arrived.
        expiredTournaments: (info as any)?.expired_tournaments ?? [],

        // Helper methods
        isFree:       info?.subscription_tier === ClubSubscriptionTier.Free,
        isBasic:      info?.subscription_tier === ClubSubscriptionTier.Basic,
        isStarter:    info?.subscription_tier === ClubSubscriptionTier.Starter,
        isPro:        info?.subscription_tier === ClubSubscriptionTier.Pro,
        isEnterprise: info?.subscription_tier === ClubSubscriptionTier.Enterprise,

        hasFeature: (feature: string): boolean => {
            return info?.features?.[feature] ?? false;
        },

        // Mutations
        checkout:       checkoutMutation.mutateAsync,
        isCheckingOut:  checkoutMutation.isPending,

        /**
         * Opens Stripe's Billing Portal — cancel, switch plan, update card, invoices.
         *
         * Returns an error string rather than throwing, and the caller must show it. This used to
         * be a bare await with no catch and no else: a 500 became an unhandled rejection and a
         * missing portal_url became nothing at all, so the button genuinely did nothing on screen
         * for every failure mode there is.
         */
        openBillingPortal: async (): Promise<string | null> => {
            try {
                const result = await billingPortalMutation.mutateAsync();
                if (result.portal_url) {
                    window.location.href = result.portal_url;
                    return null;
                }
                return 'Could not open the billing portal. Please try again.';
            } catch (e: any) {
                return e?.message || e?.error || 'Could not open the billing portal. Please try again.';
            }
        },
        isOpeningPortal: billingPortalMutation.isPending,
    };
}
